import { ClinicProduct, ListResult, SupplierPublicBrand } from '../types';
import { USE_MOCK_SUPPLIER_BRAND } from '../constants/buyer-portal';
import { supplierBrandMock } from '../mocks/supplierBrandMock';
import { clinicHttp } from './http';
import { ENDPOINTS } from './endpoints';
import { mapProductBuyerDto, ProductBuyerDtoBackend } from './productMapper';

export type SupplierProductQuery = {
  skip?: number;
  take?: number;
};

export type SupplierListQuery = {
  keyword?: string;
  skip?: number;
  take?: number;
};

export type SupplierBrandService = {
  list: (query?: SupplierListQuery) => Promise<ListResult<SupplierPublicBrand>>;
  detail: (slug: string) => Promise<SupplierPublicBrand>;
  products: (
    slug: string,
    query?: SupplierProductQuery,
  ) => Promise<ListResult<ClinicProduct>>;
};

type SupplierPublicDtoBackend = {
  publicBrandName?: string | null;
  publicSlug?: string | null;
  publicLogo?: string | null;
  publicDescription?: string | null;
};

type BackendSupplierListResult = {
  items: SupplierPublicDtoBackend[] | null;
  skip: number;
  take: number;
  total: number;
};

type BackendProductListResult = {
  items: ProductBuyerDtoBackend[] | null;
  skip: number;
  take: number;
  total: number;
};

const mapSupplierPublicDto = (s: SupplierPublicDtoBackend): SupplierPublicBrand => ({
  // Backend doesn't expose supplierId publicly — slug is the stable key.
  supplierId: s.publicSlug ?? '',
  publicSlug: s.publicSlug ?? '',
  publicBrandName: s.publicBrandName ?? '',
  publicLogo: s.publicLogo ?? undefined,
  publicDescription: s.publicDescription ?? undefined,
});

const realService: SupplierBrandService = {
  list: async (query = {}) => {
    const res = await clinicHttp.get<BackendSupplierListResult>(
      ENDPOINTS.PUBLIC.SUPPLIERS,
      { params: query },
    );
    return {
      items: (res.data.items ?? []).map(mapSupplierPublicDto),
      skip: res.data.skip,
      take: res.data.take,
      total: res.data.total,
    };
  },
  detail: async (slug) => {
    const res = await clinicHttp.get<SupplierPublicDtoBackend>(
      ENDPOINTS.PUBLIC.SUPPLIER_DETAIL(slug),
    );
    return mapSupplierPublicDto(res.data);
  },
  products: async (slug, query = {}) => {
    const res = await clinicHttp.get<BackendProductListResult>(
      ENDPOINTS.PUBLIC.SUPPLIER_PRODUCTS(slug),
      { params: query },
    );
    return {
      items: (res.data.items ?? []).map(mapProductBuyerDto),
      skip: res.data.skip,
      take: res.data.take,
      total: res.data.total,
    };
  },
};

export const supplierBrandService: SupplierBrandService = USE_MOCK_SUPPLIER_BRAND
  ? supplierBrandMock
  : realService;
